import type { ReactNode } from 'react';
import { METHOD_LABEL, formatDate, formatMoney, servicePeriodText } from '../format';
import { nextSort } from '../query';
import type { PaymentRecordItem, SortDir, SortField } from '../types';
import { ComingSoon } from './ComingSoon';
import { DownloadIcon, EyeIcon, PaidIcon, PrintIcon, SortIcon, VoidIcon } from './icons';
import { StatusBadge } from './StatusBadge';

interface Props {
  items: PaymentRecordItem[];
  sort: SortField;
  dir: SortDir;
  onSort: (next: { sort: SortField; dir: SortDir }) => void;
  /** Whether this bundle offers the record-changing actions (never in a production bundle; the server also refuses them there). */
  writesAvailable: boolean;
  onView: (item: PaymentRecordItem) => void;
  onPrint: (item: PaymentRecordItem) => void;
  onMarkPaid: (item: PaymentRecordItem, opener: HTMLElement) => void;
  onVoid: (item: PaymentRecordItem, opener: HTMLElement) => void;
}

function Header({ label, field, sort, dir, onSort, className }: { label: string; field: SortField; sort: SortField; dir: SortDir; onSort: Props['onSort']; className?: string }) {
  const direction = sort === field ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" aria-sort={direction} className={className}>
      <button type="button" className={`cp-sort${direction === 'none' ? '' : ' cp-sort--active'}`} onClick={() => onSort(nextSort({ sort, dir }, field))} title={`Sort by ${label}`}>
        {label}
        <SortIcon direction={direction} />
      </button>
    </th>
  );
}

function Act({ kind, label, name, onClick, children }: { kind: string; label: string; name: string; onClick: (el: HTMLButtonElement) => void; children: ReactNode }) {
  return (
    <button type="button" className={`cp-act cp-act--${kind}`} aria-label={name} onClick={(e) => onClick(e.currentTarget)}>
      {children}
      <span className="cp-act__label" aria-hidden="true">{label}</span>
    </button>
  );
}

/** The 1099 alert state is the server's (`threshold1099State`); the table only labels it. */
function TrackableBadge({ item }: { item: PaymentRecordItem }) {
  if (!item.form1099Trackable) return <span className="cp-badge cp-badge--no">Not 1099 Trackable</span>;
  if (item.threshold1099State === 'Reached') return <span className="cp-badge cp-badge--threshold">Threshold Reached</span>;
  if (item.threshold1099State === 'Approaching') return <span className="cp-badge cp-badge--approaching">Approaching Threshold</span>;
  return <span className="cp-badge cp-badge--yes">1099 Trackable</span>;
}

/**
 * The Figma table: Contractor / Payment Date / Service Period / Payment Method / Amount / 1099 Trackable / YTD Total / Status / Actions. The row turns pale
 * yellow on hover; each action is an icon that expands to icon + label on hover or keyboard focus (its accessible name is always the full phrase).
 * Which actions a row offers comes from the server (`allowedActions`), never from this component. Row selection (Figma checkbox column) is omitted: the
 * only bulk action, Email, is not available yet (UX / DESIGN GAP).
 */
export function RecordsTable({ items, sort, dir, onSort, writesAvailable, onView, onPrint, onMarkPaid, onVoid }: Props) {
  return (
    <div className="cp-table-wrap table-scroll" role="region" aria-label="Contractor payments table" tabIndex={0}>
      <table className="cp-table" aria-label="Contractor payments">
        <thead>
          <tr>
            <Header label="Contractor" field="contractor" sort={sort} dir={dir} onSort={onSort} />
            <Header label="Payment Date" field="paymentDate" sort={sort} dir={dir} onSort={onSort} />
            <Header label="Service Period" field="servicePeriod" sort={sort} dir={dir} onSort={onSort} />
            <Header label="Payment Method" field="paymentMethod" sort={sort} dir={dir} onSort={onSort} />
            <Header label="Amount" field="amount" sort={sort} dir={dir} onSort={onSort} className="cp-num" />
            <Header label="1099 Trackable" field="form1099" sort={sort} dir={dir} onSort={onSort} />
            <th scope="col" className="cp-num">YTD Total</th>
            <Header label="Status" field="status" sort={sort} dir={dir} onSort={onSort} />
            <th scope="col" className="cp-actions-head">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const who = `${r.paymentId}, ${r.contractorName}`;
            const canPay = writesAvailable && r.allowedActions.includes('markPaid');
            const canVoid = writesAvailable && r.allowedActions.includes('void');
            return (
              <tr key={r.id} data-testid={`record-${r.id}`} data-status={r.status}>
                <th scope="row" className="cp-contractor">
                  <span className="cp-contractor__name">{r.contractorName}</span>
                  {r.businessName && <span className="cp-sub">{r.businessName}</span>}
                  <span className="cp-sub">{r.paymentId}{r.provenance === 'Outside Escochex' ? ' · Outside Escochex' : ''}</span>
                </th>
                <td>{formatDate(r.paymentDate)}</td>
                <td>{servicePeriodText(r.servicePeriodStart, r.servicePeriodEnd)}</td>
                <td>{METHOD_LABEL[r.paymentMethod]}{r.checkNumber && <span className="cp-sub">No. {r.checkNumber}</span>}</td>
                <td className="cp-num cp-amount">{formatMoney(r.amount)}</td>
                <td><TrackableBadge item={r} /></td>
                <td className="cp-num cp-ytd">{formatMoney(r.ytdTotal)}</td>
                <td><StatusBadge status={r.status} /></td>
                <td>
                  <div className="cp-row-actions">
                    <Act kind="view" label="View" name={`View statement ${who}`} onClick={() => onView(r)}><EyeIcon /></Act>
                    <ComingSoon label={`Download statement ${who}`} reason="Download PDF is coming soon" className="cp-act cp-act--download">
                      <DownloadIcon /><span className="cp-act__label" aria-hidden="true">Download</span>
                    </ComingSoon>
                    <Act kind="print" label="Print" name={`Print statement ${who}`} onClick={() => onPrint(r)}><PrintIcon /></Act>
                    {canPay && <Act kind="paid" label="Mark as Paid" name={`Mark ${who} as paid`} onClick={(el) => onMarkPaid(r, el)}><PaidIcon /></Act>}
                    {canVoid && <Act kind="void" label="Void" name={`Void ${who}`} onClick={(el) => onVoid(r, el)}><VoidIcon /></Act>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
